import {
  canDownloadSpaceFile,
  DataRoomAccessPermission,
  DataRoomStatus,
  isPreviewableInSpace,
  MAX_SPACE_FOLDER_DEPTH,
  SpaceKind,
  SpaceSearchFolderView,
} from 'shared';

import { BreadcrumbTrailItem } from '../../../components/breadcrumb/index';
import {
  SpaceBrowserCrumb,
  SpaceBrowserPermissions,
  SpaceSelection,
} from '../types/data-room.types';

const recipientPermissionOf = ({
  isRecipientView,
  space,
}: {
  isRecipientView: boolean;
  space: SpaceSelection['space'];
}): DataRoomAccessPermission | null =>
  isRecipientView && space.kind === SpaceKind.DATA_ROOM
    ? (space.permission ?? DataRoomAccessPermission.VIEWER)
    : null;

export const canOpenBrowserFile = ({
  contentType,
  isRecipientView = false,
  space,
}: {
  contentType: string;
  isRecipientView?: boolean;
  space: SpaceSelection['space'];
}): boolean => {
  const permission = recipientPermissionOf({ isRecipientView, space });

  return !permission || canDownloadSpaceFile(permission) || isPreviewableInSpace(contentType);
};

/**
 * What the open folder lets the viewer do; the server refuses the same writes. The Vault is not a
 * space tree, an inactive data room is read-only, and a folder at the depth limit takes no more
 * sub-folders. Somebody a room was shared with gets the room itself and none of the owner's tools.
 */
export const spaceBrowserPermissions = ({
  breadcrumbs,
  isRecipientView = false,
  space,
}: { isRecipientView?: boolean } & Pick<
  SpaceSelection,
  'breadcrumbs' | 'space'
>): SpaceBrowserPermissions => {
  const isReadOnly = space.kind === SpaceKind.DATA_ROOM && space.status === DataRoomStatus.INACTIVE;
  const canManage = !isRecipientView;
  const canWrite = canManage && space.kind !== SpaceKind.VAULT && !isReadOnly;
  const permission = recipientPermissionOf({ isRecipientView, space });

  return {
    canCreateFolder: canWrite && breadcrumbs.length < MAX_SPACE_FOLDER_DEPTH,
    canDownload: !permission || canDownloadSpaceFile(permission),
    canManage,
    canWrite,
    isReadOnly,
  };
};

// The way down to a folder a search found, the folder itself last, as opening it from the table
// would have built it level by level.
export const foundFolderBreadcrumbs = ({
  ancestors,
  id,
  name,
}: SpaceSearchFolderView): SpaceBrowserCrumb[] => [
  ...ancestors.map((ancestor) => ({ id: ancestor.id, name: ancestor.name })),
  { id, name },
];

export enum BrowserContentState {
  EMPTY = 'empty',
  ERROR = 'error',
  LOADING = 'loading',
  READY = 'ready',
}

export const browserContentState = ({
  isEmpty,
  isError,
  isPending,
}: {
  isEmpty: boolean;
  isError: boolean;
  isPending: boolean;
}): BrowserContentState => {
  if (isPending) return BrowserContentState.LOADING;

  // Checked before empty: a folder that failed to load has no rows either, but is not empty.
  if (isError) return BrowserContentState.ERROR;

  return isEmpty ? BrowserContentState.EMPTY : BrowserContentState.READY;
};

export const browserTrail = ({
  breadcrumbs,
  rootLabel,
  spaceId,
}: {
  breadcrumbs: SpaceBrowserCrumb[];
  rootLabel: string;
  spaceId: string;
}): BreadcrumbTrailItem[] => [
  { id: spaceId, label: rootLabel },
  ...breadcrumbs.map((crumb) => ({ id: crumb.id, label: crumb.name })),
];
